import { COST_BREAKDOWN_DECIMAL_PRECISION } from "../constants"
import type { AmountPercentagePair } from "../types"

import type { GoodsReceipt, Product } from "@/graphql/codegen/graphql"
import type { PurchaseOrderLineQuantity, PurchaseOrderLineWithReceiptContext } from "@/screens/WarehouseReceiving/types"
import { convertPrice, isMetricUom, isUsUom } from "@/utils/conversion"
import { roundToPrecision } from "@/utils/number"

export const calculateTotalBareCost = (bareCostPerUom: number | null, quantityInUom: number): number | null => {
  if (bareCostPerUom === null || quantityInUom === 0) {
    return null
  }

  const totalBareCost = bareCostPerUom * quantityInUom
  return roundToPrecision(totalBareCost, COST_BREAKDOWN_DECIMAL_PRECISION)
}

export const calculateQuantityInUom = (purchaseOrderLine: PurchaseOrderLineWithReceiptContext): number => {
  const orderLinePalletRows = purchaseOrderLine.palletRows ?? []

  return orderLinePalletRows.reduce((sum, row) => {
    const totalUomSum = row.lots?.reduce((acc, lot) => acc + (lot.totalUom ?? 0), 0) ?? 0
    const rowTotal = totalUomSum * (row.palletQty ?? 0)
    return sum + rowTotal
  }, 0)
}

export const calculateCostBreakdownLineQuantity = (
  purchaseOrderLine: PurchaseOrderLineWithReceiptContext
): PurchaseOrderLineQuantity => {
  const product = purchaseOrderLine.product as Product
  const usUomId = product.packageUsWeightUom ?? ""
  const metricUomId = product.packageMetricWeightUom ?? ""
  const palletRows = purchaseOrderLine.palletRows ?? []

  const { palletsCount, usQuantity } = palletRows.reduce(
    (acc, palletRow) => {
      const palletQty = palletRow.palletQty ?? 0
      const palletTotal = palletRow.lots?.reduce((lotAcc, lot) => lotAcc + (lot.totalUom ?? 0), 0) ?? 0
      return {
        palletsCount: acc.palletsCount + palletQty,
        usQuantity: acc.usQuantity + palletTotal * palletQty,
      }
    },
    { palletsCount: 0, usQuantity: 0 }
  )

  const metricQuantity = usQuantity * ((product.packageMetricWeight ?? 0) / (product.packageUsWeight ?? 1))

  return {
    palletsCount: roundToPrecision(palletsCount, 2),
    regional: {
      us: {
        quantity: roundToPrecision(usQuantity, 2),
        uomId: usUomId,
      },
      metric: {
        quantity: roundToPrecision(metricQuantity, 2),
        uomId: metricUomId,
      },
    },
  }
}

// AK-6569: Unrounded — rounding here would accumulate error via the allocatedGrCostAmount calculation
// in useProductCalculations.
export const calculateAllocatedGrCostPercentage = (
  quantityInUom: number,
  goodsReceipt: GoodsReceipt
): number | null => {
  const allPalletRows = goodsReceipt.palletRows ?? []
  const totalQuantitySum = allPalletRows.reduce((sum, row) => {
    const totalUomSum = row.lots?.reduce((acc, lot) => acc + (lot.totalUom ?? 0), 0) ?? 0
    const rowTotal = totalUomSum * (row.palletQty ?? 0)
    return sum + rowTotal
  }, 0)

  return quantityInUom && totalQuantitySum > 0 ? (quantityInUom / totalQuantitySum) * 100 : null
}

// AK-6569: Unrounded — rounding here would accumulate error via the allocatedGrCostPerUom and
// freightAndMiscCostPerUom calculations in useProductCalculations.
export const calculateAllocatedGrCostAmount = (
  allocatedGrCostPercentage: number | null,
  sharedGrCost: number | null
): number | null => {
  return allocatedGrCostPercentage && sharedGrCost ? (allocatedGrCostPercentage * sharedGrCost) / 100 : null
}

// AK-6569: Unrounded — matches the backend, which also doesn't round this until persisting.
export const calculateAllocatedGrCostPerUom = (
  allocatedGrCostAmount: number | null,
  quantityInUom: number
): number | null => {
  return allocatedGrCostAmount && quantityInUom > 0 ? allocatedGrCostAmount / quantityInUom : null
}

// AK-6569: Unrounded — rounding here would accumulate error via the freightAndMiscCostPerUom calculation below.
export const calculateTariffsSum = (tariffs: AmountPercentagePair[]): number => {
  return (tariffs || []).reduce((sum: number, tariff: AmountPercentagePair) => {
    const amount = tariff?.amount || 0
    return sum + amount
  }, 0)
}

// AK-6569: Unrounded — rounding here would accumulate error via the freightAndMiscCostPerUom calculation below.
export const calculateAtypicalSurchargesSum = (
  atypicalSurcharges: { type?: any; description?: string; amount?: number }[]
): number => {
  return (atypicalSurcharges || []).reduce(
    (sum: number, surcharge: { type?: any; description?: string; amount?: number }) => {
      const amount = surcharge?.amount || 0
      return sum + amount
    },
    0
  )
}

interface TotalLaidDownCostParams {
  quantityInUom: number
  bareCostPerUom?: number | null
  insurance?: number | null
  allocatedGrCostAmount?: number | null
  dutiesAmount?: number | null
  mpfAmount?: number | null
  hmfAmount?: number | null
  tariffs?: AmountPercentagePair[]
  atypicalSurcharges?: { type?: any; description?: string; amount?: number }[]
}

// AK-6569: Unrounded — rounding here would accumulate error via the totalLaidDownCostPerUom calculation below.
export const calculateFreightAndMiscCostPerUom = ({
  quantityInUom,
  insurance,
  allocatedGrCostAmount,
  dutiesAmount,
  mpfAmount,
  hmfAmount,
  tariffs,
  atypicalSurcharges,
}: Omit<TotalLaidDownCostParams, "pricePerUom">): number | null => {
  if (!quantityInUom) return null

  let totalCost = 0
  totalCost += insurance ?? 0
  totalCost += allocatedGrCostAmount ?? 0
  totalCost += dutiesAmount ?? 0
  totalCost += mpfAmount ?? 0
  totalCost += hmfAmount ?? 0
  totalCost += calculateTariffsSum(tariffs ?? [])
  totalCost += calculateAtypicalSurchargesSum(atypicalSurcharges ?? [])

  return totalCost / quantityInUom
}

// AK-6569: Unrounded — matches the backend, which also doesn't round this until persisting.
export const calculateTotalLaidDownCostPerUom = ({
  quantityInUom,
  bareCostPerUom,
  ...rest
}: TotalLaidDownCostParams): number | null => {
  if (!quantityInUom) return null

  const freightAndMiscCostPerUom = calculateFreightAndMiscCostPerUom({ quantityInUom, ...rest })
  return (bareCostPerUom ?? 0) + (freightAndMiscCostPerUom ?? 0)
}

export const calculateBareCostPerUomFromTotalBareCost = (totalBareCost: number, quantityInUom: number): number => {
  const rawResult = totalBareCost / quantityInUom
  return roundToPrecision(rawResult, COST_BREAKDOWN_DECIMAL_PRECISION)
}

export const convertUsPriceToMetric = (
  usPrice: number,
  product: PurchaseOrderLineWithReceiptContext["product"],
  priceUomId: string
): number | null => {
  if (!product || !priceUomId) {
    return null
  }

  // If the order line's UOM is already US, convert to Metric
  if (isUsUom(priceUomId)) {
    const { amount } = convertPrice({
      currentPrice: usPrice,
      product,
      pricingUomId: priceUomId,
    })
    return roundToPrecision(amount, COST_BREAKDOWN_DECIMAL_PRECISION)
  }

  // If the order line's UOM is Metric, we need to use a US UOM for conversion
  // Use the product's US package weight UOM, or default to Pound
  const usUomId = product?.packageUsWeightUom ?? "USSTD_WEIGHT_POUND"
  const { amount } = convertPrice({
    currentPrice: usPrice,
    product,
    pricingUomId: usUomId,
  })
  return roundToPrecision(amount, COST_BREAKDOWN_DECIMAL_PRECISION)
}

export const convertMetricPriceToUs = (
  metricPrice: number,
  product: PurchaseOrderLineWithReceiptContext["product"],
  priceUomId: string
): number | null => {
  if (!product || !priceUomId) {
    return null
  }

  // If the order line's UOM is already Metric, convert to US
  if (isMetricUom(priceUomId)) {
    const { amount } = convertPrice({
      currentPrice: metricPrice,
      product,
      pricingUomId: priceUomId,
    })
    return roundToPrecision(amount, COST_BREAKDOWN_DECIMAL_PRECISION)
  }

  // If the order line's UOM is US, we need to use a Metric UOM for conversion
  // Use the product's Metric package weight UOM, or default to Kilogram
  const metricUomId = product?.packageMetricWeightUom ?? "METRIC_WEIGHT_KILOGRAM"
  const { amount } = convertPrice({
    currentPrice: metricPrice,
    product,
    pricingUomId: metricUomId,
  })
  return roundToPrecision(amount, COST_BREAKDOWN_DECIMAL_PRECISION)
}
